import { useTranslation } from 'next-i18next/pages';
import React, { type CSSProperties } from 'react';

import { AniSlideUp } from '@/components/shared/animations/basic';
import AniSlideUpBlur from '@/components/shared/animations/basic/AniSlideUpBlur';
import ArtDirectedImage, {
  type ArtDirectedImageSource,
} from '@/components/shared/ui/ArtDirectedImage';

import Area from '../../layouts/shared/Area';

type SubtopSolutionProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  subtitleClassName?: string;
  desktopImage: ArtDirectedImageSource;
  mobileImage: ArtDirectedImageSource;
  /** Dictionary key for the image's alt text, translated here */
  imageAlt?: string;
  /**
   * 원본 emotion 스타일에서도 대응되는 CSS 규칙이 없어 렌더에 영향을 주지 않는다.
   * (styled.div 의 제네릭에만 선언되어 있고 본문에서 참조되지 않던 잠재 no-op)
   */
  showShadow?: boolean;
  kind?: 'safety' | 'industrial' | 'custom';
  style?: CSSProperties;
  imageWrapperStyle?: CSSProperties;
};

/** kind 는 배경 그라디언트 CSS 변수만 덮어쓴다. 런타임 값이라 정적 lookup map 으로 둔다. */
const KIND_CLASS: Record<NonNullable<SubtopSolutionProps['kind']>, string> = {
  safety: 'subtop-solution-bg--safety',
  industrial: 'subtop-solution-bg--industrial',
  custom: '',
};

export default function SubtopSolution({
  title,
  subtitle,
  subtitleClassName,
  desktopImage,
  mobileImage,
  imageAlt = 'Listen AI 솔루션',
  kind = 'custom',
  style,
  imageWrapperStyle,
}: SubtopSolutionProps) {
  const { t } = useTranslation('common');

  return (
    // 배경은 image-set() + radial-gradient 다중 배경이라 globals.css 의 .subtop-solution-bg 로 유지
    <div
      className={`subtop-solution-bg relative ${KIND_CLASS[kind]}`}
      style={style}
    >
      <Area>
        <AniSlideUpBlur>
          <div className="flex flex-col gap-4 py-10 lg:pb-[clamp(40px,10vw,120px)] lg:pt-[clamp(40px,8.333vw,100px)] lg:tracking-[-1.2px]">
            {subtitle && (
              <p
                className={`text-center text-[24px] font-semibold leading-[140%] tracking-[-0.72px] text-apps-coreGray03 md:text-[clamp(24px,3vw,36px)] ${subtitleClassName ?? ''}`}
              >
                {subtitle}
              </p>
            )}
            <h2 className="text-center text-[32px] font-bold leading-[120%] tracking-[-0.64px] text-apps-coreGrayBlack md:text-[clamp(32px,5vw,60px)]">
              {title}
            </h2>
          </div>
        </AniSlideUpBlur>

        <AniSlideUp>
          <div
            className="relative overflow-hidden rounded-[20px] lg:rounded-[clamp(20px,3.333vw,40px)]"
            style={imageWrapperStyle}
          >
            <ArtDirectedImage
              className="block w-full [&_img]:block [&_img]:h-auto [&_img]:w-full"
              desktop={desktopImage}
              mobile={mobileImage}
              alt={t(imageAlt)}
              sizes="(min-width: 1560px) 1480px, (min-width: 992px) calc(100vw - 80px), calc(100vw - 40px)"
              quality={86}
              loading={'eager'}
              fetchPriority={'high'}
            />
          </div>
        </AniSlideUp>
      </Area>
    </div>
  );
}
